import { Icon, Styles } from '@studio/design-system/icon';
import { motion, Motions } from '@studio/design-system/motion';
import { useLatestFn } from '@studio/react-utils/hooks';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo, useEffect, useState } from 'react';
import { twMerge } from 'cn';
import type { ItemProps } from './types.js';
import { Lang } from '#~/command/index.js';
import { Mediator } from '#~/hot-keys/components/combo/index.js';
import type { Combo } from '#~/hot-keys/index.js';
import { find, Icons, toString } from '#~/hot-keys/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useOperatingSystem } from '@studio/react-utils/user-agent';

/** @internal */
const Inner = ({
  action,
  className,
  combo,
  formula,
  onCommit,
  ...props
}: ItemProps): ReactElement => {
  /* Determine if the specified key combination conflicts with any existing key combination. */
  const isValid = useLatestFn((nextValue: Combo | false): boolean => {
    const hit = nextValue !== false ? find(formula, nextValue) : undefined;

    return !hit || action === hit;
  });

  const os = useOperatingSystem();

  const [error, setError] = useState(false);

  useEffect(() => {
    setError(!isValid(combo));
  }, [combo]);

  // Set up mediator handlers.
  const handleCancel = (): void => {
    // Roll back error state to previous value.
    setError(!isValid(combo));
  };

  const handleChange = (nextCombo: Combo | false): void => {
    setError(!isValid(nextCombo));
  };

  const { track } = useAnalytics();

  const handleCommit = (nextCombo: Combo | false): void => {
    // Is the committed value valid?
    const v = isValid(nextCombo);

    // If not, treat this like a cancel
    if (!v) {
      handleCancel();

      return;
    }

    // Otherwise, commit the value.
    setError(!isValid(nextCombo));
    onCommit(action, nextCombo);

    // Track the hotkey change
    if (combo === false && nextCombo !== false) {
      // Setting a new hotkey
      track(EVENT_NAMES.SET_NEW_HOTKEY, EVENT_CATEGORIES.CONTROLLERS, {
        hotkeyCommand: action,
        hotkeyName: Lang[action]
      });
    } else if (combo !== false && nextCombo !== false) {
      // Changing an existing hotkey
      track(EVENT_NAMES.CHANGE_HOTKEY, EVENT_CATEGORIES.CONTROLLERS, {
        hotkeyCommand: action,
        hotkeyName: Lang[action],
        // Include the old and new hotkey combinations for analytics
        metadata: {
          oldHotkey: toString(os, combo),
          newHotkey: toString(os, nextCombo)
        }
      });
    }
  };

  const label = Lang[action];
  const icon = Icons[action];

  return (
    <div
      className={twMerge(
        'relative flex h-[55px] items-center gap-8 border-b border-inherit text-muted',
        className
      )}
      key={action}
      {...props}
    >
      {!!icon && <Icon className='text-xl' icon={icon} />}

      <div>{label}</div>

      <div className='relative ml-auto'>
        {!!error && (
          <motion.div
            {...Motions.fade}
            className='absolute right-[110%] top-1/2 -translate-y-1/2 whitespace-nowrap text-sm text-error'
          >
            <Icon className={Styles.larger} icon='alert-circle' /> Hot key already assigned
          </motion.div>
        )}

        <Mediator
          className={twMerge(error && '!border-error')}
          onCancel={handleCancel}
          onChange={handleChange}
          onCommit={handleCommit}
          value={combo}
        />
      </div>
    </div>
  );
};

/** `<Item>` component. */
export const Item = memo(Inner, dequal);
